import React, { useState, useMemo, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import { 
    FaEdit, FaTrashAlt, FaTimes, FaUpload, 
    FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaExchangeAlt 
} from 'react-icons/fa';
import { Helmet } from "react-helmet-async";
import './AddSlider.css';
import axiosInstance from '../../../api/axiosInstance';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5000";

// --- MODAL COMPONENT ---
const SliderFormModal = ({ isOpen, onClose, slide, mode, onSave }) => {
    const [formData, setFormData] = useState({ src: '', altText: '', isActive: true });
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            if (slide) {
                setFormData(slide);
                setPreviewUrl(slide.src ? `${API_BASE_URL}${slide.src}` : '');
                setFile(null);
            } else {
                setFormData({ src: '', altText: '', isActive: true });
                setPreviewUrl('');
                setFile(null);
            }
        }
    }, [slide, isOpen]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
        }
    };

    const handleRemoveMedia = (e) => {
        e.stopPropagation();
        setFile(null);
        setPreviewUrl('');
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = () => {
        if (mode === 'add' && !file) return Swal.fire('Error', 'Please upload an image', 'error');
        
        const submission = new FormData();
        submission.append('altText', formData.altText || '');
        submission.append('isActive', formData.isActive);
        if (file) submission.append('media', file);

        onSave(submission);
    };

    if (!isOpen) return null;
    const isView = mode === 'view';

    return (
        <div className="admin-add-slider-modal-overlay">
            <div className="admin-add-slider-modal-content" onClick={e => e.stopPropagation()}>
                <div className="admin-add-slider-modal-header">
                    <h2>{isView ? 'View Slider Details' : slide ? 'Edit Slider' : 'Add New Slider'}</h2>
                    <button className="admin-add-slider-modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="admin-add-slider-modal-body">
                    <div className="admin-add-slider-preview-container" onClick={() => !isView && fileInputRef.current.click()}>
                        {previewUrl ? (
                            <div className="admin-add-slider-media-display">
                                <img src={previewUrl} alt="Preview" onError={(e) => e.target.src='https://via.placeholder.com/300?text=Error'} />
                                {!isView && (
                                    <div className="admin-add-slider-preview-overlay">
                                        <button className="admin-add-slider-overlay-btn replace"><FaExchangeAlt /> Replace</button>
                                        <button className="admin-add-slider-overlay-btn remove" onClick={handleRemoveMedia}><FaTrashAlt /> Remove</button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="admin-add-slider-upload-prompt">
                                <FaUpload className="admin-add-slider-upload-icon"/>
                                <span>Click to Upload Image</span>
                            </div>
                        )}
                        <input type="file" ref={fileInputRef} hidden onChange={handleFileChange} accept="image/*" />
                    </div>
                    <div className="admin-add-slider-form-group">
                        <label>Image Alt Text <small>(Optional)</small></label>
                        <input 
                            type="text" 
                            name="altText" 
                            value={formData.altText} 
                            onChange={handleChange} 
                            className="admin-add-slider-input" 
                            placeholder="Enter image description (e.g., Summer Sale Banner)" 
                            disabled={isView}
                        />
                    </div>
                </div>
                <div className="admin-add-slider-modal-footer">
                    <button className="admin-add-slider-btn-cancel" onClick={onClose}>{isView ? 'Close' : 'Cancel'}</button>
                    {!isView && <button className="admin-add-slider-btn-save" onClick={handleSubmit}>Save Changes</button>}
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const AddSlider = () => {
    const [slides, setSlides] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); 
    const [selectedSlide, setSelectedSlide] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const fetchSliders = async () => {
        try {
            const res = await axiosInstance.get('/api/sliders');
            setSlides(res.data);
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error(error);
                Swal.fire('Error', 'Could not fetch sliders from the server.', 'error');
            }
        }
    };

    useEffect(() => { fetchSliders(); }, []);

    const filteredSlides = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return slides;
        return slides.filter(s =>
            (s.altText && s.altText.toLowerCase().includes(term))
        );
    }, [slides, searchTerm]);

    const totalResults = filteredSlides.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);
    const startResult = totalResults === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endResult = Math.min(currentPage * itemsPerPage, totalResults);
    const paginatedSlides = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredSlides.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredSlides, currentPage, itemsPerPage]);

    const handleSave = async (formData) => {
        const url = modalMode === 'edit' ? `/api/sliders/${selectedSlide._id}` : `/api/sliders`;
        const method = modalMode === 'edit' ? 'put' : 'post';

        try {
            await axiosInstance({ method, url, data: formData });
            await fetchSliders();
            Swal.fire(modalMode === 'edit' ? 'Updated!' : 'Added!', '', 'success');
            setIsModalOpen(false);
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
            }
        }
    };

    const handleDelete = (id) => {
        Swal.fire({ title: 'Delete Slider?', text: 'Cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' }).then(async res => {
            if (res.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/sliders/${id}`);
                    await fetchSliders();
                    Swal.fire('Deleted', '', 'success');
                } catch (error) {
                    if (error.response?.status !== 401) {
                        Swal.fire('Error', error.response?.data?.message || 'Delete failed', 'error');
                    }
                }
            }
        });
    };

    const toggleActive = async (id) => {
        const slide = slides.find(s => s._id === id);
        const newStatus = !slide.isActive;
        
        try {
            await axiosInstance.put(`/api/sliders/${id}`, { isActive: newStatus });
            await fetchSliders();
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', 'Failed to toggle status', 'error');
            }
        }
    };

    const openModal = (mode, slide = null) => {
        setModalMode(mode);
        setSelectedSlide(slide);
        setIsModalOpen(true);
    };

    return (
        <>
        <Helmet>
                    <title>Add Slider | Rajput Shiksha Kosh</title>
            
                    <meta
                      name="description"
                      content="Manage hero sliders for the Rajput Shiksha Kosh organization."
                    />
                  </Helmet>
        <div className="admin-add-slider-wrapper">
            <div className="admin-add-slider-container">
                <SliderFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} slide={selectedSlide} mode={modalMode} onSave={handleSave} />
                
                <h2 className="admin-add-slider-title">Hero Sliders Management</h2>

                <div className="admin-add-slider-controls">
                    <div className="admin-add-slider-search-wrapper">
                        <FaSearch className="admin-add-slider-search-icon" />
                        <input type="text" placeholder="Search by alt text..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="admin-add-slider-search-input" />
                    </div>
                    <div className="admin-add-slider-action-group">
                        <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="admin-add-slider-limit-select">
                            <option value="10">10 / page</option>
                            <option value="20">20 / page</option>
                            <option value="30">30 / page</option>
                            <option value="50">50 / page</option>
                        </select>
                        <button className="admin-add-slider-add-btn" onClick={() => openModal('add')}>
                            <FaPlus /> Add New Slider
                        </button>
                    </div>
                </div>

                <div className="admin-add-slider-table-card">
                    <div className="admin-add-slider-table-wrapper">
                        <table className="admin-add-slider-table">
                            <thead>
                                <tr>
                                    <th className="admin-add-slider-col-sno">S.No</th>
                                    <th className="admin-add-slider-col-media">Image Preview</th>
                                    <th className="admin-add-slider-col-alttext">Alt Text</th>
                                    <th className="admin-add-slider-col-date">Created At</th>
                                    <th className="admin-add-slider-col-active">Active</th>
                                    <th className="admin-add-slider-col-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedSlides.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="admin-add-slider-no-data">No sliders found. Click "Add New Slider" to create one.</td>
                                    </tr>
                                ) : (
                                    paginatedSlides.map((slide, index) => (
                                        <React.Fragment key={slide._id}>
                                            <tr className="admin-add-slider-row">
                                                <td className="admin-add-slider-col-sno">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                <td className="admin-add-slider-col-media">
                                                    <div className="admin-add-slider-media-wrapper">
                                                        <div className="admin-add-slider-thumbnail" onClick={() => openModal('view', slide)}>
                                                            <img src={`${API_BASE_URL}${slide.src}`} alt={slide.altText || 'thumb'} onError={(e) => e.target.src='https://via.placeholder.com/80?text=Err'}/>
                                                        </div>
                                                        <button className="admin-add-slider-mobile-toggle-btn" onClick={() => toggleRow(slide._id)}>
                                                            {expandedRow === slide._id ? <FaChevronUp /> : <FaChevronDown />}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="admin-add-slider-col-alttext">
                                                    {slide.altText ? <span className="admin-add-slider-text-value">{slide.altText}</span> : <span className="admin-add-slider-empty">No Alt Text Provided</span>}
                                                </td>
                                                <td className="admin-add-slider-col-date">{formatDate(slide.createdAt)}</td>
                                                <td className="admin-add-slider-col-active">
                                                    <label className="admin-add-slider-switch">
                                                        <input type="checkbox" checked={slide.isActive !== false} onChange={() => toggleActive(slide._id)} />
                                                        <span className="admin-add-slider-slider-toggle" />
                                                    </label>
                                                    <div className={`admin-add-slider-status-text ${slide.isActive !== false ? 'active' : 'disabled'}`}>
                                                        {slide.isActive !== false ? 'Active' : 'Disabled'}
                                                    </div>
                                                </td>
                                                <td className="admin-add-slider-col-actions">
                                                    <div className="admin-add-slider-action-icons">
                                                        <button className="admin-add-slider-icon-btn edit" onClick={() => openModal('edit', slide)} title="Edit"><FaEdit /></button>
                                                        <button className="admin-add-slider-icon-btn delete" onClick={() => handleDelete(slide._id)} title="Delete"><FaTrashAlt /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedRow === slide._id && (
                                                <tr className="admin-add-slider-details-row">
                                                    <td colSpan="6">
                                                        <div className="admin-add-slider-details-content">
                                                            <div className="admin-add-slider-details-grid">
                                                                <div className="admin-add-slider-detail-card admin-add-slider-dc-alttext">
                                                                    <div className="admin-add-slider-detail-label">Alt Text</div>
                                                                    <div className="admin-add-slider-detail-value">{slide.altText || '-'}</div>
                                                                </div>
                                                                <div className="admin-add-slider-detail-card admin-add-slider-dc-date-created">
                                                                    <div className="admin-add-slider-detail-label">Created At</div>
                                                                    <div className="admin-add-slider-detail-value">{formatDate(slide.createdAt)}</div>
                                                                </div>
                                                                <div className="admin-add-slider-detail-card admin-add-slider-dc-active">
                                                                    <div className="admin-add-slider-detail-label">Status</div>
                                                                    <label className="admin-add-slider-switch">
                                                                        <input type="checkbox" checked={slide.isActive !== false} onChange={() => toggleActive(slide._id)} />
                                                                        <span className="admin-add-slider-slider-toggle" />
                                                                    </label>
                                                                </div>
                                                                <div className="admin-add-slider-detail-card admin-add-slider-dc-actions">
                                                                    <div className="admin-add-slider-detail-label">Actions</div>
                                                                    <div className="admin-add-slider-action-icons" style={{ justifyContent: 'flex-start' }}>
                                                                        <button className="admin-add-slider-icon-btn edit" onClick={() => openModal('edit', slide)}><FaEdit /> Edit</button>
                                                                        <button className="admin-add-slider-icon-btn delete" onClick={() => handleDelete(slide._id)}><FaTrashAlt /> Delete</button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                {paginatedSlides.length > 0 && (
                    <div className="admin-add-slider-pagination-container">
                        <div className="admin-add-slider-pagination-info">Showing {startResult} to {endResult} of {totalResults} results</div>
                        <div className="admin-add-slider-pagination-controls">
                            <button className="admin-add-slider-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                            <span className="admin-add-slider-page-count">Page {currentPage} of {totalPages || 1}</span>
                            <button className="admin-add-slider-page-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default AddSlider;